import React, { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import api from "@/lib/api";

/**
 * GoogleReviewsBlock — social-proof strip pulled from Google Places for a
 * dispensary. Renders rating + count + up to 5 recent reviews. Silently
 * hides when the API returns no data (e.g. the shop isn't on Google, or
 * the operator hasn't enabled the Places API yet).
 */
const StarRow = ({ value = 0, size = 14 }) => {
  const v = Math.round((value || 0) * 2) / 2;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${v} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          width={size}
          height={size}
          className={
            i <= v ? "fill-[#f0b938] text-[#f0b938]" : "text-foreground/25"
          }
        />
      ))}
    </span>
  );
};

export const GoogleReviewsBlock = ({ slug, testId }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api
      .get(`/dispensaries/${slug}/google-reviews`)
      .then((r) => {
        if (!cancelled) setData(r.data || null);
      })
      .catch(() => setData(null))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!loaded || !data || !data.rating) return null;

  return (
    <section
      data-testid={testId || `google-reviews-${slug}`}
      className="border border-border bg-card rounded-3xl p-6 sm:p-8"
    >
      <header className="flex flex-wrap items-center gap-3 justify-between mb-4">
        <div>
          <div className="eyebrow mb-1">From Google</div>
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl">
              {data.rating?.toFixed?.(1) ?? data.rating}
            </span>
            <StarRow value={data.rating} size={16} />
            <span className="text-sm text-foreground/60">
              {data.rating_count?.toLocaleString?.() ?? data.rating_count} reviews
            </span>
          </div>
        </div>
        {data.google_maps_uri && (
          <a
            href={data.google_maps_uri}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="text-xs text-foreground/60 hover:text-foreground inline-flex items-center gap-1"
          >
            See on Google Maps <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </header>

      {data.reviews?.length > 0 && (
        <div className="space-y-4">
          {data.reviews.map((r, i) => (
            <article
              key={i}
              data-testid={`google-review-${i}`}
              className="border-t border-border/60 pt-4 first:border-t-0 first:pt-0"
            >
              <div className="flex items-center gap-2 flex-wrap mb-1.5 text-xs">
                <StarRow value={r.rating} size={12} />
                <span className="text-foreground/70">{r.author}</span>
                {r.published_ago && (
                  <span className="text-foreground/40">· {r.published_ago}</span>
                )}
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed line-clamp-4">
                {r.text}
              </p>
            </article>
          ))}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-4">
        Google reviews shown per Google Maps Platform terms. Bud Link does
        not edit, filter, or endorse them.
      </p>
    </section>
  );
};

export default GoogleReviewsBlock;
